// API service for backend communication
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Generic API request function
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    headers: getAuthHeaders(),
    ...options
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        // Clear stored tokens
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        
        // Redirect to login page
        window.location.href = '/login';
        throw new Error('Authentication expired. Please log in again.');
      }
      
      throw new Error(data.message || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

// Authentication API calls
export const authAPI = {
  login: (email, password) => 
    apiRequest('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    }),

  register: (userData) => 
    apiRequest('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    }),

  getProfile: () => 
    apiRequest('/api/auth/profile'),

  updateProfile: (profileData) => 
    apiRequest('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    }),

  changePassword: (currentPassword, newPassword) => 
    apiRequest('/api/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword })
    }),

  forgotPassword: (email) => 
    apiRequest('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    })
};

// Partners API calls
export const partnersAPI = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/partners?${queryString}`);
  },

  getById: (id) => 
    apiRequest(`/api/partners/${id}`),

  create: (partnerData) => 
    apiRequest('/api/partners', {
      method: 'POST',
      body: JSON.stringify(partnerData)
    }),

  update: (id, partnerData) => 
    apiRequest(`/api/partners/${id}`, {
      method: 'PUT',
      body: JSON.stringify(partnerData)
    }),

  delete: (id) => 
    apiRequest(`/api/partners/${id}`, {
      method: 'DELETE'
    }),

  assignExtension: (partnerId, extensionId, isWhitelabeled = false) => 
    apiRequest(`/api/partners/${partnerId}/extensions`, {
      method: 'POST',
      body: JSON.stringify({ extensionId, isWhitelabeled })
    }),

  removeExtension: (partnerId, extensionId) => 
    apiRequest(`/api/partners/${partnerId}/extensions/${extensionId}`, {
      method: 'DELETE'
    })
};

// Extensions API calls
export const extensionsAPI = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/extensions?${queryString}`);
  },

  getById: (id) => 
    apiRequest(`/api/extensions/${id}`),

  create: (extensionData) => 
    apiRequest('/api/extensions', {
      method: 'POST',
      body: JSON.stringify(extensionData)
    }),

  update: (id, extensionData) => 
    apiRequest(`/api/extensions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(extensionData)
    }),

  delete: (id) => 
    apiRequest(`/api/extensions/${id}`, {
      method: 'DELETE'
    }),

  getIcon: (id) => 
    apiRequest(`/api/extensions/${id}/icon`),

  getDownload: (id) => 
    apiRequest(`/api/extensions/${id}/download`),

  getStats: (id) => 
    apiRequest(`/api/extensions/${id}/stats`)
};

// Users API calls (Admin only)
export const usersAPI = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/admin/users?${queryString}`);
  },

  getById: (id) => 
    apiRequest(`/api/admin/users/${id}`),

  create: (userData) => 
    apiRequest('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    }),

  update: (id, userData) => 
    apiRequest(`/api/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    }),

  delete: (id) => 
    apiRequest(`/api/admin/users/${id}`, {
      method: 'DELETE'
    }),

  // Suspend user
  suspend: (id) => 
    apiRequest(`/api/admin/users/${id}/suspend`, {
      method: 'POST'
    }),

  // Unsuspend user
  unsuspend: (id) => 
    apiRequest(`/api/admin/users/${id}/unsuspend`, {
      method: 'POST'
    }),

  // Update user plan
  updatePlan: (id, plan) => 
    apiRequest(`/api/admin/users/${id}/plan`, {
      method: 'PUT',
      body: JSON.stringify({ plan })
    }),

  // Extensions
  getExtensions: () => 
    apiRequest('/api/extensions'),

  // Extension Users
  getEndUsers: (extensionId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/admin/extensions/${extensionId}/users?${queryString}`);
  },

  createEndUser: (extensionId, userData) => 
    apiRequest(`/api/admin/extensions/${extensionId}/users`, {
      method: 'POST',
      body: JSON.stringify(userData)
    })
};

// Statistics API calls
export const statisticsAPI = {
  getDashboardStats: () => 
    apiRequest('/api/admin/statistics')
};

// External API calls (for webhooks)
export const externalAPI = {
  createPartner: (partnerData) => 
    apiRequest('/api/external/create-partner', {
      method: 'POST',
      body: JSON.stringify(partnerData)
    }),

  suspendPartner: (email) => 
    apiRequest('/api/external/suspend-partner', {
      method: 'POST',
      body: JSON.stringify({ email })
    }),

  unsuspendPartner: (email) => 
    apiRequest('/api/external/unsuspend-partner', {
      method: 'POST',
      body: JSON.stringify({ email })
    }),

  addUpdateUser: (userData) => 
    apiRequest('/api/external/add-update-user', {
      method: 'POST',
      body: JSON.stringify(userData)
    }),

  suspendUser: (email) => 
    apiRequest('/api/external/suspend-user', {
      method: 'POST',
      body: JSON.stringify({ email })
    }),

  unsuspendUser: (email) => 
    apiRequest('/api/external/unsuspend-user', {
      method: 'POST',
      body: JSON.stringify({ email })
    }),

  deleteUser: (email) => 
    apiRequest('/api/external/delete-user', {
      method: 'POST',
      body: JSON.stringify({ email })
    })
};

// Health check
export const healthCheck = () => 
  apiRequest('/health');

export default {
  auth: authAPI,
  partners: partnersAPI,
  extensions: extensionsAPI,
  users: usersAPI,
  external: externalAPI,
  healthCheck
}; 